<template>
    <common-content-spacer height="medium" class="availability-time-picker-container">
        <div v-for="(moments, index) in momentsArr" :key="index" class="availability-time-picker">
            <button
                v-for="(moment, indexM) in moments"
                :key="indexM"
                class="availability-time-picker_button"
                :disabled="isMomentDisabled(moment)"
                v-bind="momentProps(moment)"
                @click="handleClick(moment)"
            >
                <span class="availability-time-picker__tooltip" :data-tooltip="momentDate(moment)"></span>

                <span class="availability-time-picker__moment-label">
                    {{ momentDate(moment) }}
                </span>

                <svg v-if="momentIcon(moment)">
                    <use :xlink:href="momentIcon(moment)" />
                </svg>
            </button>
        </div>
        <common-buttons class="availability-time-picker__group-buttons">
            <common-button
                type="secondary"
                :isDisabled="areActionsDisabled"
                :isSubmittable="false"
                :label="$t('common.microcopy.decline')"
                @click="declineSlots()"
            ></common-button>
            <common-button
                type="primary"
                :isSubmittable="false"
                :isDisabled="areActionsDisabled"
                :label="$t('common.microcopy.accept')"
                @click="acceptSlots()"
            ></common-button>
        </common-buttons>
    </common-content-spacer>
</template>

<script>
    import dayjs from 'dayjs'
    import isEqual from 'lodash.isequal'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    export default {
        mixins: [WindowResizeMixin],

        props: {
            appointmentLength: { type: Number, required: true },
            originalSlots: { type: Array, default: new Array() },
        },

        data() {
            return {
                draftSlot: { from: undefined, to: undefined },
                selectedSlots: [],
                timelineCount: 1,
            }
        },

        computed: {
            momentsArr() {
                const ONE_HOUR = 60
                const slotLength = this.appointmentLength % ONE_HOUR === 0 ? ONE_HOUR : ONE_HOUR / 2
                const start = dayjs().startOf('day')
                return this.timelines
                    .map((time) => ({
                        from: start.add(time.timeFrom, 'hour'),
                        to: start.add(time.timeTo, 'hour'),
                        timeFrom: time.timeFrom,
                        timeTo: time.timeTo,
                    }))
                    .map((time) => ({
                        value: (time.to.diff(time.from, 'hour') * 60) / slotLength + 1,
                        timeFrom: time.timeFrom,
                        timeTo: time.timeTo,
                    }))
                    .map((moments) =>
                        Array(moments.value)
                            .fill({})
                            .map((option, index) =>
                                start
                                    .add(moments.timeFrom, 'hour')
                                    .add(slotLength * index, 'minute')
                                    .diff(start, 'minute'),
                            ),
                    )
            },

            slots() {
                return this.selectedSlots.concat([this.draftSlot])
            },

            isDraftMode() {
                return this.draftSlot.from !== undefined || this.draftSlot.to !== undefined
            },

            areActionsDisabled() {
                return isEqual(this.originalSlots, this.selectedSlots)
            },

            timelines() {
                if (this.timelineCount === 3) {
                    return [
                        { timeFrom: 0, timeTo: 8 },
                        { timeFrom: 8, timeTo: 16 },
                        { timeFrom: 16, timeTo: 24 },
                    ]
                } else if (this.timelineCount === 2) {
                    return [
                        { timeFrom: 0, timeTo: 12 },
                        { timeFrom: 12, timeTo: 24 },
                    ]
                }
                return [{ timeFrom: 0, timeTo: 24 }]
            },
        },

        methods: {
            momentDate(moment) {
                return dayjs().utcOffset(0).startOf('day').add(moment, 'minute').format('HH:mm')
            },

            isFullHour(moment) {
                return moment % 60 === 0
            },

            isSlotStart(moment) {
                return this.slots.some((slot) => moment === slot.from)
            },

            isSlotWithin(moment) {
                return this.slots.some((slot) => moment > slot.from && moment < slot.to)
            },

            isSlotEnd(moment) {
                return this.slots.some((slot) => moment === slot.to)
            },

            isSlotChanged(moment) {
                const slot = this.selectedSlots.find((s) => s.from === moment || s.to === moment)

                if (slot) {
                    if (this.isSlotStart(moment)) {
                        return !!slot.isFromChanged
                    } else if (this.isSlotEnd(moment)) {
                        return !!slot.isToChanged
                    }
                }
                return false
            },

            isSlotDraft(moment) {
                return [this.draftSlot.from, this.draftSlot.to].includes(moment)
            },

            isOverlappingSlot(from, to) {
                const testedSlot = {
                    from: Math.min(from, to),
                    to: Math.max(from, to),
                }

                return this.selectedSlots.some(
                    (slot) =>
                        (testedSlot.from < slot.from && slot.from < testedSlot.to) ||
                        (testedSlot.from >= slot.from && testedSlot.from <= slot.to) ||
                        (testedSlot.to >= slot.from && testedSlot.to <= slot.to),
                )
            },

            addSlot(from, to) {
                this.selectedSlots = this.selectedSlots
                    .concat([
                        {
                            from: Math.min(from, to),
                            to: Math.max(from, to),
                        },
                    ])
                    .sort((s1, s2) => s1.from - s2.from)
            },

            removeSlot(moment) {
                this.selectedSlots = this.selectedSlots
                    .filter((slot) => {
                        return ![slot.from, slot.to].includes(moment)
                    })
                    .sort((s1, s2) => s1.from - s2.from)
            },

            clearDraftSlot() {
                this.draftSlot = { from: undefined, to: undefined }
            },

            momentProps(moment) {
                return {
                    type: 'button',
                    class: {
                        'availability-time-picker__moment': true,
                        'availability-time-picker__moment--hour-full': this.isFullHour(moment),
                        'availability-time-picker__moment--hour-within': !this.isFullHour(moment),
                        'availability-time-picker__moment--slot-start': this.isSlotStart(moment),
                        'availability-time-picker__moment--slot-within': this.isSlotWithin(moment),
                        'availability-time-picker__moment--slot-end': this.isSlotEnd(moment),
                        'availability-time-picker__moment--slot-draft': this.isSlotDraft(moment),
                        'availability-time-picker__moment--slot-changed': this.isSlotChanged(moment),
                        'availability-time-picker__moment--disabled': this.isMomentDisabled(moment),
                    },
                }
            },

            momentIcon(moment) {
                if (this.isSlotStart(moment)) {
                    return '#picture-slider-left'
                }

                if (this.isSlotEnd(moment)) {
                    return '#picture-slider-right'
                }

                return false
            },

            handleClick(moment) {
                const isSlotStart = this.isSlotStart(moment)
                const isSlotWithin = this.isSlotWithin(moment)
                const isSlotEnd = this.isSlotEnd(moment)
                const isOverlappingSlot = this.isOverlappingSlot(this.draftSlot.from, moment)

                if (this.isDraftMode) {
                    if (isSlotStart || isSlotWithin || isSlotEnd || isOverlappingSlot) {
                        this.clearDraftSlot()
                        return
                    }
                    this.addSlot(this.draftSlot.from, moment)
                    this.clearDraftSlot()
                    return
                }
                if (isSlotStart || isSlotEnd) {
                    this.removeSlot(moment)
                    return
                }

                if (!this.isOverlappingSlot(moment, moment)) {
                    this.draftSlot.from = moment
                }
            },

            acceptSlots() {
                this.emitEvent({ name: 'slots-selected', payload: this.selectedSlots })
            },

            declineSlots() {
                this.selectedSlots = this.originalSlots
            },

            modifyTimelines() {
                const MAX_TIMELINE_COUNT = 3
                const MIN_TIMELINE_COUNT = 1
                const MAX_ELEMENT_SIZE = 30
                const el = document.querySelector('.availability-time-picker_button:not(:first-child)')
                if (el?.offsetWidth < MAX_ELEMENT_SIZE && this.timelineCount < MAX_TIMELINE_COUNT) {
                    this.timelineCount = this.timelineCount + 1
                } else if (el?.offsetWidth > 2 * MAX_ELEMENT_SIZE && this.timelineCount > MIN_TIMELINE_COUNT) {
                    this.timelineCount = this.timelineCount - 1
                }
            },

            isMomentDisabled(moment) {
                if (!this.isDraftMode || this.isSlotStart(moment) || this.isSlotWithin(moment) || this.isSlotEnd(moment)) {
                    return false
                }
                return Math.abs(moment - this.draftSlot.from) % this.appointmentLength !== 0
            },
        },

        watch: {
            selectedSlots() {
                this.clearDraftSlot()
            },
            originalSlots() {
                this.selectedSlots = this.originalSlots
            },
        },

        mounted() {
            setTimeout(() => {
                this.modifyTimelines()
                if (this.isSmartphoneLarge) {
                    this.modifyTimelines()
                }
            }, 0)
            window.addEventListener('resize', this.modifyTimelines)
        },

        destroyed() {
            window.removeEventListener('resize', this.modifyTimelines)
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/availability/_availability-time-picker.scss';
</style>
